// Chrome-free PDF embed for form PdfBlock elements (docs/decisions/forms.md).
// Pages render to canvases sized to the container, with PDF.js's annotation
// layer on top so links keep working: web links open in a new tab, internal
// links scroll to their page. Fillable fields are not rendered as inputs (the
// form cannot capture their values), and PDF JavaScript never runs.
//
// Mirrors moxie-app's src/modules/forms/pdfRenderer.js (PDF.js 5) on PDF.js 2.16,
// the last release webpack 4 bundles cleanly. This file is identical in
// struxture-public and struxture-clientportal; keep all three in step. No
// optional chaining or nullish coalescing: the Vue 2 unit-test build parses
// source untranspiled.

let pdfjsPromise = null;
const loadPdfjs = () => {
	if (!pdfjsPromise) {
		pdfjsPromise = Promise.all([
			import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf'),
			// file-loader emits the worker as its own asset (Vue CLI 4 ships it).
			import('!!file-loader?name=js/[name].[contenthash:8].[ext]!pdfjs-dist/legacy/build/pdf.worker.min.js'),
		]).then(([pdfjs, worker]) => {
			pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;
			return pdfjs;
		});
	}
	return pdfjsPromise;
};

const MAX_CANVAS_PIXELS = 16 * 1024 * 1024;
// Mounted embeds, so printing can draw pages that never scrolled into view.
const activeEmbeds = new Set();

/** Renders every page of every mounted embed; await before window.print(). */
export function renderAllPdfEmbeds() {
	return Promise.all([...activeEmbeds].map((embed) => embed.renderAll().catch(noop)));
}
const noop = () => {};
const eventBus = { dispatch: noop, on: noop, off: noop, _on: noop, _off: noop };

function linkServiceFor(state) {
	return {
		eventBus,
		externalLinkEnabled: true,
		addLinkAttributes(link, url) {
			link.href = url;
			link.title = url;
			link.target = '_blank';
			link.rel = 'noopener noreferrer nofollow';
		},
		getDestinationHash: () => '#',
		getAnchorUrl: () => '#',
		goToDestination: (dest) => scrollToDestination(state, dest),
		executeNamedAction(action) {
			const last = state.pages.length - 1;
			const target = { FirstPage: 0, LastPage: last, NextPage: state.lastLinkedPage + 1, PrevPage: state.lastLinkedPage - 1 }[action];
			if (target !== undefined) scrollToPage(state, Math.max(0, Math.min(last, target)));
		},
		executeSetOCGState: noop,
	};
}

async function scrollToDestination(state, dest) {
	try {
		const explicit = typeof dest === 'string' ? await state.doc.getDestination(dest) : dest;
		const ref = Array.isArray(explicit) ? explicit[0] : null;
		if (ref === null || ref === undefined) return;
		const index = typeof ref === 'object' ? await state.doc.getPageIndex(ref) : Number(ref);
		scrollToPage(state, index);
	} catch {
		// An unresolvable destination simply does nothing, as in PDF viewers.
	}
}

function scrollToPage(state, index) {
	const entry = state.pages[index];
	if (entry) entry.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function renderPage(state, entry) {
	const width = state.container.clientWidth;
	if (!width || state.destroyed || entry.renderedWidth === width) return;
	entry.renderedWidth = width;
	if (entry.task) entry.task.cancel();

	const base = entry.page.getViewport({ scale: 1 });
	const viewport = entry.page.getViewport({ scale: width / base.width });
	const ratio = Math.min(window.devicePixelRatio || 1, Math.sqrt(MAX_CANVAS_PIXELS / (viewport.width * viewport.height)));
	const canvas = document.createElement('canvas');
	canvas.width = Math.floor(viewport.width * ratio);
	canvas.height = Math.floor(viewport.height * ratio);
	canvas.className = 'pdf-embed-canvas';
	const transform = ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null;

	entry.task = entry.page.render({ canvasContext: canvas.getContext('2d'), viewport, transform });
	try {
		await entry.task.promise;
	} catch (err) {
		if (err && err.name === 'RenderingCancelledException') return;
		throw err;
	}
	if (state.destroyed) return;
	if (entry.canvas) entry.canvas.remove();
	entry.el.prepend(canvas);
	entry.canvas = canvas;

	if (!entry.annotationsRendered) {
		entry.annotationsRendered = true;
		// Links only: comments, pop-ups and form widgets have no place in a form.
		const annotations = (await entry.page.getAnnotations({ intent: 'display' })).filter((item) => item.annotationType === state.pdfjs.AnnotationType.LINK);
		if (!annotations.length || state.destroyed) return;
		const layer = document.createElement('div');
		layer.className = 'annotationLayer';
		entry.el.append(layer);
		const linkService = linkServiceFor(state);
		layer.addEventListener('click', () => (state.lastLinkedPage = entry.index), true);
		state.pdfjs.AnnotationLayer.render({
			viewport: viewport.clone({ dontFlip: true }),
			div: layer,
			annotations,
			page: entry.page,
			linkService,
			renderForms: false,
			enableScripting: false,
		});
		// Sections are positioned in percentages, so the layer simply fills the page.
		layer.style.width = '100%';
		layer.style.height = '100%';
	}
}

/**
 * Renders `url` into `container`. Returns { destroy }.
 * `onState` receives 'loading' | 'ready' | 'error'.
 */
export function mountPdfEmbed(container, url, { onState = noop } = {}) {
	const state = { container, pages: [], doc: null, pdfjs: null, destroyed: false, lastLinkedPage: 0 };
	let loadingTask = null;
	let resizeTimer = null;
	let intersection = null;
	let resize = null;
	onState('loading');

	const renderVisible = () => state.pages.filter((entry) => entry.visible).forEach((entry) => renderPage(state, entry).catch(noop));

	const ready = (async () => {
		state.pdfjs = await loadPdfjs();
		if (state.destroyed) return;
		// isEvalSupported: false closes CVE-2024-4367 on PDF.js releases before 4.2.
		loadingTask = state.pdfjs.getDocument({ url, isEvalSupported: false, enableXfa: false });
		state.doc = await loadingTask.promise;
		if (state.destroyed) return;

		const pages = await Promise.all(Array.from({ length: state.doc.numPages }, (_, i) => state.doc.getPage(i + 1)));
		if (state.destroyed) return;
		container.innerHTML = '';
		state.pages = pages.map((page, index) => {
			const { width, height } = page.getViewport({ scale: 1 });
			const el = document.createElement('div');
			el.className = 'pdf-embed-page';
			// Padding reserves the page height before it renders (older iOS has no aspect-ratio).
			el.style.paddingTop = `${(height / width) * 100}%`;
			container.append(el);
			return { page, index, el, visible: false, renderedWidth: 0, canvas: null, task: null, annotationsRendered: false };
		});

		intersection = new IntersectionObserver(
			(records) => {
				for (const record of records) {
					const entry = state.pages.find((item) => item.el === record.target);
					if (!entry) continue;
					entry.visible = record.isIntersecting;
					if (entry.visible) renderPage(state, entry).catch(noop);
				}
			},
			{ rootMargin: '600px 0px' },
		);
		state.pages.forEach((entry) => intersection.observe(entry.el));

		resize = new ResizeObserver(() => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(renderVisible, 150);
		});
		resize.observe(container);
		onState('ready');
	})().catch(() => {
		if (!state.destroyed) onState('error');
	});

	const embed = {
		async renderAll() {
			await ready;
			await Promise.all(state.pages.map((entry) => renderPage(state, entry)));
		},
		destroy() {
			activeEmbeds.delete(embed);
			state.destroyed = true;
			clearTimeout(resizeTimer);
			if (intersection) intersection.disconnect();
			if (resize) resize.disconnect();
			state.pages.forEach((entry) => entry.task && entry.task.cancel());
			if (loadingTask) loadingTask.destroy();
		},
	};
	activeEmbeds.add(embed);
	return embed;
}
